import React from 'react';
import format from 'string-template';
import { connect } from 'react-redux';
import { length } from 'redux-form-validators';
import { isEmpty, toPlainObject } from 'lodash';
import { formValueSelector, reset as resetForm } from 'redux-form';

import {
    FIRSTNAME_LENGTH,
    NAME_LENGTH,
    ADDRESS_LENGTH,
    POSTCODE_LENGTH,
    CITY_LENGTH,
    PHONE_LENGTH
} from '../../../../../constants/form';
import { STATIC_ROOT } from '../../../../../constants';
import { MONSIEUR, MADAME } from '../../../../../constants/civilities';
import { PAYMENT_REQUEST_TYPE_IBAN } from '../../../../../services/account/paymentrequest/request/constants';

import {
    sendPaymentRequest,
    reset,
    setTimeoutId
} from '../../../../../services/account/paymentrequest/request/actions';

import { getValidatedAmount } from '../../../../../services/account/gains/selector';
import { getOnGoingPaidAmount } from '../../../../../services/account/paymentrequest/ongoing/selector';
import * as paymentRequestSelector from '../../../../../services/account/paymentrequest/request/selector';

import { convertHtmlToReactDomWithParams } from '../../../../../utils/htmlUtils';
import { formatAmountCurrency, mapCountries, truncateAmount } from '../../../../../utils';

import {
    BICInputFieldComponent,
    FormComponent,
    IBANInputFieldComponent,
    LegendFieldComponent,
    NumberInputFieldComponent,
    PhoneInputFieldComponent,
    SelectFieldComponent,
    TextInputFieldComponent
} from '../../../../form';
import SvgComponent from '../../../../SvgComponent';

import './PaymentRequestFormComponent.scss';

const FORM_NAME = 'paymentrequest-form';

class PaymentRequestIbanFormComponent extends React.Component {
    componentDidMount() {
        this.scrollTop();
    }

    scrollTop = () => {
        if (document.getElementById('paymentAreaAnchor')) {
            document.getElementById('paymentAreaAnchor').scrollIntoView({ behavior: 'smooth' });
        }
    };

    submit = formData => {
        const { sendPaymentRequest, resetForm, reset, timeoutId, setTimeoutId } = this.props;

        clearTimeout(timeoutId);

        sendPaymentRequest(PAYMENT_REQUEST_TYPE_IBAN, formData, () => {
            const id = setTimeout(() => {
                reset();
            }, 20000);
            setTimeoutId(id);

            resetForm(FORM_NAME);
            this.scrollTop();
        });
    };

    validate = values => {
        const { i18nCommon } = this.props;
        const errors = {};

        if (!values.civility) {
            errors.civility = i18nCommon.OPTION_REQUIRED;
        }

        return errors;
    };

    render() {
        const {
            i18n,
            i18nCommon,
            errorSubmit,
            errorMessage,
            validatedCashbackAmount,
            paymentRequestTotalAmount,
            requestedAmount,
            COUNTRIES,
            CIVILITIES,
            requestSubmit,
            initialValues
        } = this.props;

        const validatedMinusPaymentRequest = truncateAmount(
            validatedCashbackAmount - paymentRequestTotalAmount
        );
        const remainingJackpotAfterPayment =
            validatedMinusPaymentRequest - requestedAmount < 0
                ? 0
                : validatedMinusPaymentRequest - requestedAmount;

        return (
            <div className="payment-request iban">
                <FormComponent
                    form={FORM_NAME}
                    initialValues={initialValues}
                    classNameInputContainer="payment-request-form"
                    onSubmit={this.submit}
                    requestSubmit={requestSubmit}
                    errorSubmit={errorSubmit}
                    submitText={i18n.PAYMENT_REQUEST_FORM.IBAN.PAYMENT_REQUEST_BUTTON_IBAN}>
                    <React.Fragment>
                        <div className="grid-2 has-gutter full">
                            <SelectFieldComponent
                                name="countryIban"
                                label={i18nCommon.IBAN_CODE_IBAN}
                                isRequired
                                options={COUNTRIES}
                                classNameInputContainer="one-third"
                                i18nFeatureForm={i18nCommon}
                            />
                        </div>
                    </React.Fragment>

                    <React.Fragment>
                        <div className="grid-2 has-gutter full text-align-iban">
                            <IBANInputFieldComponent
                                name="iban"
                                placeholder={i18nCommon.IBAN_CODE_DETAIL}
                                i18nFeatureForm={i18nCommon}
                            />
                            <LegendFieldComponent classNameInputContainer="format-iban-container">
                                <div className="format-iban">{i18nCommon.IBAN_FORMAT}</div>
                            </LegendFieldComponent>
                        </div>
                    </React.Fragment>

                    <React.Fragment>
                        <div className="grid-2 has-gutter full">
                            <BICInputFieldComponent
                                name="bic"
                                placeholder={i18nCommon.CODE_BIC_DETAIL}
                                label={i18nCommon.CODE_BIC}
                                isRequired
                                classNameInputContainer="one-third"
                                i18nFeatureForm={i18nCommon}
                            />
                        </div>
                    </React.Fragment>

                    <NumberInputFieldComponent
                        name="requestedAmount"
                        label={i18n.PAYMENT_REQUEST_FORM.IBAN.AMOUNT_TO_ACCOUNT}
                        placeholder={i18n.PAYMENT_REQUEST_FORM.IBAN.EXAMPLE_AMOUNT}
                        classNameInputContainer="amount"
                        min={0.1}
                        max={validatedMinusPaymentRequest}
                        isRequired
                        hideRequired
                        isEuro
                        showSymbol={'€'}
                    />
                    <React.Fragment>
                        <div className="information-div">
                            <span>
                                <SvgComponent
                                    src={`${STATIC_ROOT}/info.svg`}
                                    width="20"
                                    height="20"
                                    aria-hidden="true"
                                    focusable="false"
                                />
                            </span>
                            <span>
                                {convertHtmlToReactDomWithParams(
                                    i18n.PAYMENT_REQUEST_FORM
                                        .REMAINING_JACKPOT_AFTER_PAYMENT_INDICATION,
                                    formatAmountCurrency(remainingJackpotAfterPayment)
                                )}
                            </span>
                        </div>
                    </React.Fragment>

                    <React.Fragment>
                        <div className="grid-2 has-gutter full">
                            <SelectFieldComponent
                                name="civility"
                                label={i18nCommon.LABEL_CIVILITY}
                                isRequired
                                defaultOptionLabel="--"
                                classNameInputContainer="one-third mr50"
                                options={[
                                    { label: CIVILITIES.MONSIEUR, value: MONSIEUR },
                                    { label: CIVILITIES.MADAME, value: MADAME }
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />

                            <TextInputFieldComponent
                                name="lastname"
                                placeholder={i18nCommon.LABEL_LASTNAME}
                                label={i18nCommon.LABEL_LASTNAME}
                                isRequired
                                validate={[
                                    length({
                                        max: NAME_LENGTH,
                                        msg: format(
                                            i18nCommon.TEXT_TOOLONG,
                                            NAME_LENGTH,
                                            NAME_LENGTH > 1 ? 's' : ''
                                        )
                                    })
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />

                            <TextInputFieldComponent
                                name="firstname"
                                placeholder={i18nCommon.LABEL_FIRSTNAME}
                                label={i18nCommon.LABEL_FIRSTNAME}
                                isRequired
                                validate={[
                                    length({
                                        max: FIRSTNAME_LENGTH,
                                        msg: format(
                                            i18nCommon.TEXT_TOOLONG,
                                            FIRSTNAME_LENGTH,
                                            FIRSTNAME_LENGTH > 1 ? 's' : ''
                                        )
                                    })
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />

                            <TextInputFieldComponent
                                name="address"
                                classNameInputContainer="one-half"
                                placeholder={i18nCommon.LABEL_ADDRESS}
                                label={i18nCommon.LABEL_ADDRESS}
                                isRequired
                                validate={[
                                    length({
                                        max: ADDRESS_LENGTH,
                                        msg: format(i18nCommon.ADDRESS_SIZE_ERROR, ADDRESS_LENGTH)
                                    })
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />

                            <TextInputFieldComponent
                                name="zipcode"
                                classNameInputContainer="one-quarter"
                                placeholder={i18nCommon.LABEL_ZIPCODE}
                                label={i18nCommon.LABEL_ZIPCODE}
                                maxLength={POSTCODE_LENGTH}
                                isRequired
                                validate={[
                                    length({
                                        max: POSTCODE_LENGTH,
                                        msg: format(i18nCommon.ZIP_CODE_SIZE_ERROR, POSTCODE_LENGTH)
                                    })
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />

                            <TextInputFieldComponent
                                name="city"
                                classNameInputContainer="one-quarter"
                                placeholder={i18nCommon.LABEL_CITY}
                                label={i18nCommon.LABEL_CITY}
                                isRequired
                                validate={[
                                    length({
                                        max: CITY_LENGTH,
                                        msg: format(i18nCommon.CITY_SIZE_ERROR, CITY_LENGTH)
                                    })
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />
                            <SelectFieldComponent
                                name="country"
                                classNameInputContainer="one-half"
                                label={i18nCommon.LABEL_COUNTRY}
                                options={COUNTRIES}
                                isRequired
                                i18nFeatureForm={i18nCommon}
                            />

                            <PhoneInputFieldComponent
                                name="phoneNumber"
                                label={i18nCommon.LABEL_PHONENUMBER}
                                classNameInputContainer="one-half"
                                validate={[
                                    length({
                                        max: PHONE_LENGTH,
                                        msg: format(i18nCommon.PHONENUMBER_SIZE_ERROR, PHONE_LENGTH)
                                    })
                                ]}
                                i18nFeatureForm={i18nCommon}
                            />
                        </div>
                    </React.Fragment>

                    <LegendFieldComponent classNameInputContainer="full">
                        {i18nCommon.MANDATORY_FIELD}
                    </LegendFieldComponent>
                    <React.Fragment>
                        {errorMessage && (
                            <LegendFieldComponent classNameInputContainer="color-err">
                                {errorMessage}
                            </LegendFieldComponent>
                        )}
                    </React.Fragment>
                </FormComponent>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const user = toPlainObject(state.account.user);
    const isAuthed = !isEmpty(state.authentication.token);
    const validatedCashbackAmount = getValidatedAmount(state);
    const paymentRequestTotalAmount = getOnGoingPaidAmount(state);
    const requestedAmount = formValueSelector(FORM_NAME)(state, 'requestedAmount') || 0;

    return {
        i18n: state.i18n.page.ACCOUNT.CASHBACK_JACKPOT,
        i18nCommon: state.i18n.feature.form,
        requestSubmit: paymentRequestSelector.isRequesting(state),
        errorSubmit: paymentRequestSelector.isError(state),
        errorMessage: paymentRequestSelector.getError(state),
        successSubmit: paymentRequestSelector.isSuccess(state),
        user: state.account.user,
        CIVILITIES: state.i18n.feature.form.CIVILITIES,
        COUNTRIES: mapCountries(state.i18n.feature.form.COUNTRIES),
        validatedCashbackAmount: validatedCashbackAmount,
        paymentRequestTotalAmount: paymentRequestTotalAmount,
        requestedAmount: requestedAmount,
        timeoutId: paymentRequestSelector.getTimeoutId(state),
        initialValues: isAuthed
            ? {
                  countryIban: user.country ? user.country : '',
                  civility: user.civility ? user.civility : null,
                  lastname: user.lastname ? user.lastname : '',
                  firstname: user.firstname ? user.firstname : '',
                  address: user.address ? user.address : '',
                  zipcode: user.postcode ? user.postcode : '',
                  city: user.city ? user.city : '',
                  country: user.country ? user.country : '',
                  phoneNumber: user.mobileNumber ? user.mobileNumber : '',
                  iban: ''
              }
            : {}
    };
};

const mapDispatchToProps = {
    sendPaymentRequest,
    resetForm,
    reset,
    setTimeoutId
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentRequestIbanFormComponent);



// WEBPACK FOOTER //
// ./src/components/pages/account/cashback/payment-requests/PaymentRequestIbanFormComponent.js